/**
 * The seven formations a flight can be sent up in, and where each one puts the
 * aeroplanes.
 *
 * **The words are the game's own.** `PS.EXE` carries them as a contiguous
 * seven-entry table immediately before `regular`/`incendiary` and
 * `one`/`two`/`three`/`four` — `solo`, `line abreast`, `line astern`,
 * `echelon`, `vee wing`, `diamond`, `box` — and `RBMB.EXE` repeats it.
 * docs/game/missions.md §*Mission setup parameters* transcribes six of them and
 * drops `box`; the binary has seven and so does this. The manual's Dogfight a
 * Squadron section lists exactly the six non-`solo` words
 * (`manual/04-gameplay.md`: *"you may choose between line abreast, line astern,
 * echelon, vee wing, diamond and box formation"*), because a squadron fight is
 * never flown alone.
 *
 * **The rule is the game's own too.** `RBMB.EXE` validates with *"A single
 * plane may only fly in solo formation."* — so `solo` is the only legal
 * formation for a flight of one, and the only illegal one for a flight of more.
 * `formationsFor` is that sentence as a function.
 *
 * **The geometry is port-side, and stated rather than hidden.** No decoded file
 * carries station offsets in metres; what the original draws is a formation
 * *thumbnail* on the Flight Assignments screen, and this port has no capture of
 * one to measure. So each shape below is the real wartime pattern the word
 * names, laid out on a spacing that keeps wingtips clear at the speeds these
 * aeroplanes fly. What is not invented is which shapes exist or what they are
 * called.
 *
 * Offsets are in the leader's own frame before heading is applied: `+x` is off
 * his right wing, `+z` is astern of him, `+y` is above. `station(0)` is always
 * the leader, at the origin, for every formation — so a flight of one is
 * placed identically no matter which word is on the screen.
 */

import type { Vec3 } from "../flight/math3d.ts";

/** Verbatim `PS.EXE`, in the binary's own order. */
export const FORMATIONS = [
  "solo",
  "line abreast",
  "line astern",
  "echelon",
  "vee wing",
  "diamond",
  "box",
] as const;

export type Formation = (typeof FORMATIONS)[number];

/** Wingtip-clear lateral spacing, in metres. */
const SPAN_M = 110;
/** How far back a trailing station sits. */
const TRAIL_M = 90;
/** Stacked formations step down as well as back, so nobody is flying in the leader's blind spot. */
const STACK_M = 25;

/**
 * Station offsets for positions 1..3 behind the leader, per formation. Index 0
 * of each list is the second aeroplane, and a flight never has more than
 * `MAX_FLIGHT_AIRCRAFT` (`raid-parts.ts`), so three entries is the whole of it.
 */
const STATIONS: Readonly<Record<Formation, readonly Vec3[]>> = {
  // A single aeroplane has no stations at all; the entry exists so every
  // formation can be looked up without the caller special-casing this one.
  solo: [],
  // Wingtip to wingtip, all abreast: leader, then alternating right and left.
  "line abreast": [
    { x: SPAN_M, y: 0, z: 0 },
    { x: -SPAN_M, y: 0, z: 0 },
    { x: 2 * SPAN_M, y: 0, z: 0 },
  ],
  // Nose to tail, each stepped down out of the man ahead's slipstream.
  "line astern": [
    { x: 0, y: -STACK_M, z: TRAIL_M },
    { x: 0, y: -2 * STACK_M, z: 2 * TRAIL_M },
    { x: 0, y: -3 * STACK_M, z: 3 * TRAIL_M },
  ],
  // A stepped diagonal off one wing — the working patrol formation.
  echelon: [
    { x: SPAN_M, y: -STACK_M, z: TRAIL_M },
    { x: 2 * SPAN_M, y: -2 * STACK_M, z: 2 * TRAIL_M },
    { x: 3 * SPAN_M, y: -3 * STACK_M, z: 3 * TRAIL_M },
  ],
  // The classic V: two wingmen back and out, a fourth filling the slot behind.
  "vee wing": [
    { x: SPAN_M, y: 0, z: TRAIL_M },
    { x: -SPAN_M, y: 0, z: TRAIL_M },
    { x: 0, y: -STACK_M, z: 2 * TRAIL_M },
  ],
  // The V closed up by a tail-end aeroplane sitting above and behind.
  diamond: [
    { x: SPAN_M, y: 0, z: TRAIL_M },
    { x: -SPAN_M, y: 0, z: TRAIL_M },
    { x: 0, y: STACK_M, z: 2 * TRAIL_M },
  ],
  // Two pairs, stacked — the mutual-support box a flight fights in.
  box: [
    { x: SPAN_M, y: 0, z: 0 },
    { x: 0, y: -STACK_M, z: TRAIL_M },
    { x: SPAN_M, y: -STACK_M, z: TRAIL_M },
  ],
};

/**
 * The formations a flight of this size may legally fly, `RBMB.EXE`'s rule
 * exactly: `solo` alone for one aeroplane, everything but `solo` for more.
 */
export function formationsFor(flightSize: number): readonly Formation[] {
  return flightSize <= 1 ? ["solo"] : FORMATIONS.filter((entry) => entry !== "solo");
}

export function isFormationLegal(formation: Formation, flightSize: number): boolean {
  return formationsFor(flightSize).includes(formation);
}

/**
 * The formation a flight of this size flies unless the player picks another:
 * `line abreast`, the formation aeroplanes actually merge in.
 *
 * **This choice has teeth, and the numbers are worth stating.** The engine
 * picks each pilot's quarry as the *nearest* live opponent, with no
 * de-confliction (`combat/quarry.ts`'s `primaryOpponent`) — so a formation that
 * puts the leader alone at the front of a stack hands every enemy in the flight
 * the same target, and that target is the player. A 12-seed census of Dogfight a
 * Squadron, one formation at a time and everything else held equal, measured the
 * player's own aeroplane destroyed:
 *
 * | formation | player lost |
 * |---|---|
 * | `line abreast` | 6/12 |
 * | `box` | 6/12 |
 * | `vee wing` | 10/12 |
 * | `line astern` | 11/12 |
 * | `echelon` | 11/12 |
 * | `diamond` | 12/12 |
 *
 * The port's other air-to-air missions lose the player in 6 to 13 sorties out of
 * 24, so the two abreast formations put a squadron fight where the rest of the
 * game already is, and the trailing ones make it a death sentence. Picking a
 * formation is therefore a real tactical decision rather than a label — which is
 * the whole point of wiring the field — and the default is the one that does not
 * quietly volunteer the player as everybody's target.
 */
export function defaultFormation(flightSize: number): Formation {
  return flightSize <= 1 ? "solo" : "line abreast";
}

/**
 * Where aeroplane `index` sits, in world metres, given its leader's position and
 * heading. `index` 0 is the leader and always returns his own position, so the
 * caller can place a whole flight through one function.
 *
 * `headingRad` 0 points at the enemy (`mission-world.ts`'s convention: `−z` is
 * over the wire), and the offsets above are written in that frame, so the
 * rotation below is the standard one about the vertical axis.
 */
export function formationStation(
  formation: Formation,
  index: number,
  leader: Vec3,
  headingRad: number,
): Vec3 {
  if (index <= 0) return leader;
  const offsets = STATIONS[formation];
  const offset = offsets[index - 1] ?? offsets.at(-1);
  if (!offset) return leader;
  const cos = Math.cos(headingRad);
  const sin = Math.sin(headingRad);
  return {
    x: leader.x + offset.x * cos + offset.z * sin,
    y: leader.y + offset.y,
    z: leader.z - offset.x * sin + offset.z * cos,
  };
}
